/**
 * `sanitizePrefillDomainParam` — THE dashboard-side validation for a
 * `prefill_domain` carried in a URL or posted on sign-in (qa-review on
 * #570: this grammar was being inlined per call site with no parity
 * pin; `initial-view.ts`'s own header documents why hand-copied
 * mirrors are banned).
 *
 * Mirrors the SERVER's `sanitizePrefillDomain` (`auth/magic-link.ts`):
 * trim, lowercase, ≤253 chars, strict RFC-1035-shaped hostname with at
 * least one dot. Deliberately NOT the site-create `DOMAIN_RE` (which
 * requires an alphabetic TLD): the carry chain must accept exactly what
 * the auth server accepts, or a domain the server stored on the
 * magic-link row dies silently one step from the finish line
 * (qa #570 minor 5). Site creation applies its own stricter validation
 * at submit time.
 */

/** Same shape as the server's HOSTNAME_RE. */
export const PREFILL_DOMAIN_RE =
  /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/;

export function sanitizePrefillDomainParam(raw: string | null): string | null {
  if (raw === null) return null;
  const candidate = raw.trim().toLowerCase();
  if (candidate.length === 0 || candidate.length > 253) return null;
  return PREFILL_DOMAIN_RE.test(candidate) ? candidate : null;
}

/**
 * True when the current URL is a report-page CLAIM entry
 * (`/sign-in?prefill_domain=…`, ADR-0201 amendment). Claim entries are
 * publicly shareable links with NO email round-trip behind them, so the
 * carry chain must never auto-create from one — it always ends at a
 * visible, one-click offer. The post-verify carry (`/onboarding?…`)
 * keeps auto-create: clicking the emailed magic link IS the proof of
 * intent (qa #570 round 2, findings 2+7).
 */
export function isClaimEntry(): boolean {
  if (typeof window === 'undefined') return false;
  // Lowercased: browsers preserve pathname case, and a mixed-case
  // `/SIGN-IN` must not slip past this guard while the human-legible
  // spec says "the /sign-in path" (security review on #570, minor 1).
  // Load-bearing invariant: the pathname is stable for the tab's
  // session — App's setView persistence rewrites only the search
  // params via replaceState, never the pathname. If navigation ever
  // starts rewriting pathnames mid-session, snap this at mount
  // instead of re-reading per call.
  const path = window.location.pathname.toLowerCase();
  return path === '/sign-in' || path.startsWith('/sign-in/');
}
